import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useMapContext } from '../context/MapContext';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAllNodes,
  selectAllPoints,
  selectEditorEdgeEndNode,
  selectEditorEdgeStartNode,
} from '../slice/mapSelectors';
import { fromLonLat } from 'ol/proj';
import { mapActions } from '../slice/mapSlice';
import {
  useCreateInterconnectMutation,
  useCreateUpdateEdgeMutation,
} from '../../services/edgeApi';
import toast from 'react-hot-toast';
import { generateColor } from '../../utils/colors';
import { useTheme } from '../../helpers/hooks';
import { ReusableModal } from '../../modals/ReusableModal';
import { LoadingButton } from '../../helpers/LoadingButton';

export const EdgeCreationOverlay = (_props) => {
  const dispatch = useDispatch();
  const [createEdgeTrigger, createEdgeRequestInfo] =
    useCreateUpdateEdgeMutation();
  const [createInterconnectTrigger, createInterconnectInfo] =
    useCreateInterconnectMutation();
  const {
    map,
    setCursorStyle,
    registerClickListener,
    unregisterClickListener,
  } = useMapContext();
  const [theme] = useTheme();

  /** @type {React.MutableRefObject<HTMLCanvasElement>} */
  const edgeCanvasRef = useRef();

  const nodes = useSelector(selectAllNodes);
  const points = useSelector(selectAllPoints);
  const edgeStart = useSelector(selectEditorEdgeStartNode);
  const edgeEnd = useSelector(selectEditorEdgeEndNode);

  /** @type {import('@/types').State<{startNode: import('@/types').Node, endNode: Node}>} */
  const [interconnectNodes, setInterconnectNodes] = useState(null);
  /** @type {import('@/types').State<import('@/types').Point>} */
  const [interconnectStartPoint, setInterconnectStartPoint] = useState(null);
  /** @type {import('@/types').State<import('@/types').Point>} */
  const [interconnectEndPoint, setInterconnectEndPoint] = useState(null);

  const interconnectStartPoints = useMemo(() => {
    if (!interconnectNodes) return [];
    return interconnectNodes.startNode.points
      .map((pointId) => points[pointId])
      .filter(Boolean);
  }, [interconnectNodes, points]);

  const interconnectEndPoints = useMemo(() => {
    if (!interconnectNodes) return [];
    return interconnectNodes.endNode.points
      .map((pointId) => points[pointId])
      .filter(Boolean);
  }, [interconnectNodes, points]);

  const createEdge = async (startNodeId, endNodeId) => {
    if (!startNodeId || !endNodeId) {
      toast.error('Invalid nodes selected for edge creation');
      return;
    }
    if (startNodeId === endNodeId) {
      toast.error('Cannot create an edge to the same node');
      return;
    }
    if (createEdgeRequestInfo.isLoading) return;
    try {
      dispatch(mapActions.setEditorEdgeEndNode(endNodeId));
      const createEdgeResponse = await createEdgeTrigger({
        start_node: startNodeId,
        end_node: endNodeId,
      });
      if (createEdgeResponse.data) {
        toast.success('Edge created successfully');
        dispatch(mapActions.setEditorEdgeStartNode(endNodeId));
      }
    } catch (error) {
      console.error('Failed to create edge:', error);
      toast.error(error?.data?.message ?? 'Failed to create edge');
    }
    dispatch(mapActions.setEditorEdgeEndNode(null));
  };

  const createInterconnect = async () => {
    if (!interconnectStartPoint || !interconnectEndPoint) {
      toast.error(
        'Please select both start and end points for the interconnect',
      );
      return;
    }
    try {
      const createInterconnectResponse = await createInterconnectTrigger({
        start_point: interconnectStartPoint.id,
        end_point: interconnectEndPoint.id,
      });
      if (createInterconnectResponse.data) {
        toast.success('Interconnect created successfully');
        dispatch(mapActions.setEditorEdgeStartNode(null));
        setInterconnectNodes(null);
        setInterconnectStartPoint(null);
        setInterconnectEndPoint(null);
      }
    } catch (error) {
      console.error('Failed to create interconnect:', error);
      toast.error(error?.data?.message ?? 'Failed to create interconnect');
    }
  };

  useEffect(() => {
    /** @type {import('../EdgeMap').MapEventListener} */
    const clickListener = async (feature) => {
      if (!edgeStart) return;
      const nodeId =
        feature && feature.getProperties()?.key?.startsWith('node-')
          ? feature.getProperties()?.id
          : null;
      const node = nodes?.[nodeId];

      if (node) {
        const startTsp = points[edgeStart.points[0]]?.tsp_id;
        const endTsp = points[node.points[0]]?.tsp_id;
        if (startTsp === endTsp) {
          createEdge(edgeStart.id, nodeId);
        } else {
          setInterconnectNodes({ startNode: edgeStart, endNode: node });
        }
      } else {
        dispatch(mapActions.setEditorEdgeStartNode(null));
      }
    };
    registerClickListener(clickListener);
    return () => {
      unregisterClickListener(clickListener);
    };
  }, [registerClickListener, unregisterClickListener, edgeStart, nodes]);

  useEffect(() => {
    if (!map) return;
    const updateEdge = (event) => {
      const { coordinate } = event;
      const ctx = edgeCanvasRef.current.getContext('2d');
      ctx.clearRect(
        0,
        0,
        edgeCanvasRef.current.width,
        edgeCanvasRef.current.height,
      );
      if (edgeStart) {
        const tsp = points[edgeStart.points[0]]?.tsp_id;
        const tspColor = tsp
          ? theme === 'light'
            ? generateColor(tsp)
            : generateColor(tsp, 50, 40)
          : 'green';
        const edgeStartScreenPos = map.getPixelFromCoordinate(
          fromLonLat([edgeStart.lng, edgeStart.lat]),
        );
        const edgeEndScreenPos = map.getPixelFromCoordinate(
          fromLonLat([edgeEnd?.lng ?? 0, edgeEnd?.lat ?? 0]),
        );
        const coordinateScreenPos = map.getPixelFromCoordinate(coordinate);
        ctx.beginPath();
        ctx.moveTo(edgeStartScreenPos[0], edgeStartScreenPos[1]);
        if (createEdgeRequestInfo.isLoading) {
          ctx.lineTo(edgeEndScreenPos[0], edgeEndScreenPos[1]);
          ctx.strokeStyle = tspColor;
          ctx.globalAlpha = 0.5;
        } else {
          ctx.lineTo(coordinateScreenPos[0], coordinateScreenPos[1]);
          ctx.strokeStyle = 'red';
        }
        ctx.lineWidth = 5;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.stroke();
      } else {
        ctx.clearRect(
          0,
          0,
          edgeCanvasRef.current.width,
          edgeCanvasRef.current.height,
        );
      }
    };
    map.on('pointermove', updateEdge);
    map.on('change', updateEdge);
    return () => {
      map.un('pointermove', updateEdge);
      map.un('change', updateEdge);
    };
  }, [
    map,
    points,
    edgeStart,
    edgeEnd,
    edgeCanvasRef,
    createEdgeRequestInfo,
    generateColor,
  ]);

  useEffect(() => {
    if (createEdgeRequestInfo.isLoading) {
      setCursorStyle('wait');
    } else {
      setCursorStyle(null);
    }
  }, [createEdgeRequestInfo.isLoading, setCursorStyle]);

  if (!map) {
    return <></>;
  }

  return (
    <>
      <canvas
        ref={edgeCanvasRef}
        width={map.getTargetElement().offsetWidth - 6}
        height={map.getTargetElement().offsetHeight - 8}
        style={{
          position: 'absolute',
          top: map.getTargetElement().offsetTop,
          left: map.getTargetElement().offsetLeft,
          pointerEvents: 'none',
          zIndex: 1000,
          overflow: 'hidden',
        }}
      />
      <ReusableModal
        isOpen={interconnectNodes !== null}
        onClose={() => {
          setInterconnectNodes(null);
          setInterconnectStartPoint(null);
          setInterconnectEndPoint(null);
        }}
        size="lg"
      >
        <h4 className="text-center mb-3">Create Interconnect</h4>
        <div className="row mb-3">
          <div className="col-6 d-flex flex-column">
            <h5 className="text-center">Start Point</h5>
            <div>
              {interconnectStartPoints.map((point) => {
                const selected = interconnectStartPoint?.id === point.id;
                return (
                  <div
                    disabled={createInterconnectInfo.isLoading}
                    key={point.id}
                    className={`border d-flex flex-column p-2 my-1 ${
                      selected ? 'bg-secondary text-white' : ''
                    }`}
                    onClick={() => setInterconnectStartPoint(point)}
                    role="button"
                  >
                    <span className="fw-bold">{point.name}</span>
                    <span>Loc ID: {point.loc_id}</span>
                    <span>Type: {point.loc_type_ind}</span>
                    <span>Direction: {point.dir_flo}</span>
                    <span>Connects To: {point.up_dn_name}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="col-6 d-flex flex-column">
            <h5 className="text-center">End Point</h5>
            <div>
              {interconnectEndPoints.map((point) => {
                const selected = interconnectEndPoint?.id === point.id;
                return (
                  <div
                    disabled={createInterconnectInfo.isLoading}
                    key={point.id}
                    className={`border d-flex flex-column p-2 my-1 ${
                      selected ? 'bg-secondary text-white' : ''
                    }`}
                    onClick={() => setInterconnectEndPoint(point)}
                    role="button"
                  >
                    <span className="fw-bold">{point.name}</span>
                    <span>Loc ID: {point.loc_id}</span>
                    <span>Type: {point.loc_type_ind}</span>
                    <span>Direction: {point.dir_flo}</span>
                    <span>Connects To: {point.up_dn_name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div
          style={{ position: 'sticky', bottom: 0 }}
          className="justify-content-center d-flex"
        >
          <LoadingButton
            className="btn btn-primary"
            isLoading={createInterconnectInfo.isLoading}
            disabled={
              !interconnectStartPoint ||
              !interconnectEndPoint ||
              createInterconnectInfo.isLoading
            }
            onClick={createInterconnect}
          >
            Create
          </LoadingButton>
        </div>
      </ReusableModal>
    </>
  );
};
